import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Activity, Users, Building2, ExternalLink,
  CheckCircle2, XCircle, Clock, Shield, ChevronRight, Layers
} from 'lucide-react';

import { API_URL } from '../utils/api';

const ACCESS_STATUS_MAP = {
  admin: { icon: Shield, label: 'Admin Access', color: 'text-[#2C4C3B]', bg: 'bg-[#2C4C3B]/10' },
  active: { icon: CheckCircle2, label: 'Active', color: 'text-green-700', bg: 'bg-green-50' },
  trial: { icon: Clock, label: 'Trial', color: 'text-blue-700', bg: 'bg-blue-50' },
  denied: { icon: XCircle, label: 'Access Denied', color: 'text-red-700', bg: 'bg-red-50' },
  none: { icon: XCircle, label: 'No Access', color: 'text-[#595959]', bg: 'bg-[#F3F4F1]' },
};

export default function InterventionsLaunchPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchContext();
  }, []);

  const fetchContext = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/mtss/context`, { withCredentials: true });
      setContext(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load product context');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-[#2C4C3B] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-[#595959]">Loading product context...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-6">
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 max-w-md text-center">
          <XCircle className="h-10 w-10 text-red-400 mx-auto mb-4" />
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C] mb-2">Unable to Load</h2>
          <p className="text-sm text-[#595959] mb-6">{error}</p>
          <Link to="/dashboard">
            <Button variant="outline" className="rounded-xl" data-testid="error-back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const accessInfo = context?.access || {};
  const statusConfig = ACCESS_STATUS_MAP[accessInfo.status] || ACCESS_STATUS_MAP.none;
  const StatusIcon = statusConfig.icon;
  const hasAccess = accessInfo.has_access;
  const product = context?.product || {};
  const org = context?.organization;
  const enrolledCount = context?.enrolled_students_count || 0;
  const integrationNotes = context?.integration_notes || {};

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E5E5]">
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard"
                className="flex items-center gap-2 text-[#595959] hover:text-[#1C1C1C] transition-colors"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="h-5 w-5" />
                <span className="text-sm font-medium hidden sm:inline">FrameEd</span>
              </Link>
              <div className="h-6 w-px bg-[#E5E5E5]" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#2C4C3B15' }}>
                  <Activity className="h-4 w-4 text-[#2C4C3B]" />
                </div>
                <span className="font-outfit font-semibold text-[#1C1C1C]">{product.display_name || 'Frame Interventions'}</span>
              </div>
            </div>
            <Badge className={`${statusConfig.bg} ${statusConfig.color} text-xs`}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {statusConfig.label}
            </Badge>
          </div>
        </div>
      </nav>

      {/* Main */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {/* Product Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: '#2C4C3B15' }}
            >
              <Activity className="h-7 w-7 text-[#2C4C3B]" />
            </div>
            <div>
              <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-[#1C1C1C]" data-testid="product-title">
                {product.display_name || 'Frame Interventions'}
              </h1>
              <p className="text-sm text-[#595959]">MTSS & Intervention Tracking</p>
            </div>
          </div>
        </div>

        {/* Access Gate */}
        {!hasAccess ? (
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 text-center" data-testid="no-access-card">
            <XCircle className="h-10 w-10 text-[#595959] mx-auto mb-4" />
            <h2 className="font-outfit text-lg font-semibold text-[#1C1C1C] mb-2">Access Required</h2>
            <p className="text-sm text-[#595959] mb-4">
              Your organization does not currently have access to Frame Interventions.
              Contact your administrator to enable this product.
            </p>
            <Link to="/dashboard">
              <Button variant="outline" className="rounded-xl" data-testid="no-access-back-btn">
                Back to Dashboard
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Context Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Access */}
              <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="access-card">
                <div className="flex items-center gap-2 mb-3">
                  <StatusIcon className={`h-5 w-5 ${statusConfig.color}`} />
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#595959]">Access</span>
                </div>
                <p className="font-outfit text-xl font-semibold text-[#1C1C1C]">{statusConfig.label}</p>
                <p className="text-xs text-[#595959] mt-1">
                  {accessInfo.tier ? `${accessInfo.tier} tier` : 'Full access'}
                </p>
              </div>

              {/* Organization */}
              <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="org-card">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-5 w-5 text-[#3B6A9B]" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#595959]">Organization</span>
                </div>
                <p className="font-outfit text-xl font-semibold text-[#1C1C1C]">
                  {org?.name || 'All Organizations'}
                </p>
                <p className="text-xs text-[#595959] mt-1">{org?.type || 'Admin scope'}</p>
              </div>

              {/* Students Enrolled */}
              <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="enrolled-students-card">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-5 w-5 text-[#3A5A3A]" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#595959]">Enrolled</span>
                </div>
                <p className="font-outfit text-xl font-semibold text-[#1C1C1C]">{enrolledCount}</p>
                <p className="text-xs text-[#595959] mt-1">Students in MTSS</p>
              </div>
            </div>

            {/* Product Features (from registry) */}
            {product.features && product.features.length > 0 && (
              <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="features-card">
                <h3 className="font-outfit text-base font-semibold text-[#1C1C1C] mb-4">Product Capabilities</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-[#2C4C3B]" />
                      <span className="text-sm text-[#595959]">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Integration Bridge Notice */}
            <div className="bg-[#F3F4F1] border border-[#E5E5E5] rounded-2xl p-6" data-testid="integration-notice">
              <div className="flex items-start gap-3">
                <Layers className="h-5 w-5 text-[#2C4C3B] mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-outfit text-base font-semibold text-[#1C1C1C] mb-1">
                    Ecosystem Integration Point
                  </h3>
                  <p className="text-sm text-[#595959] leading-relaxed">
                    This page is the FrameEd ecosystem bridge to Frame Interventions.
                    Your authentication, organization, and student identities are shared
                    from FrameEd Core. The full MTSS product experience is available
                    through the product launch below.
                  </p>
                  {integrationNotes.deferred && integrationNotes.deferred.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
                      <p className="text-xs font-semibold text-[#595959] uppercase tracking-wider mb-2">Pending Integration</p>
                      <ul className="space-y-1">
                        {integrationNotes.deferred.map((note, i) => (
                          <li key={i} className="text-xs text-[#595959] flex items-start gap-1.5">
                            <span className="text-[#A7C7E7] mt-0.5">&#8226;</span>
                            {note}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Launch Action */}
            <div className="bg-white border border-[#2C4C3B] rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="launch-card">
              <div>
                <h3 className="font-outfit text-lg font-semibold text-[#1C1C1C]">
                  Launch Frame Interventions
                </h3>
                <p className="text-sm text-[#595959] mt-1">
                  Open the full MTSS tracking and intervention management experience.
                </p>
              </div>
              <Button
                className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white rounded-xl px-6 shrink-0"
                data-testid="launch-product-btn"
                disabled
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Launch Product
              </Button>
            </div>
            <p className="text-xs text-center text-[#595959]">
              Full product launch will be available once the standalone Frame Interventions
              app is connected via SSO handoff.
            </p>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link to="/students" className="flex-1">
                <Button variant="outline" className="w-full rounded-xl border-[#E5E5E5] hover:border-[#2C4C3B]" data-testid="manage-students-btn">
                  <Users className="h-4 w-4 mr-2" />
                  Manage Students
                  <ChevronRight className="h-4 w-4 ml-auto" />
                </Button>
              </Link>
              <Link to="/dashboard" className="flex-1">
                <Button variant="outline" className="w-full rounded-xl border-[#E5E5E5] hover:border-[#2C4C3B]" data-testid="back-dashboard-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to FrameEd
                  <ChevronRight className="h-4 w-4 ml-auto" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
